import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Button from '../Button/Button';
import { openColumnCustomizationDrawer, resetColumnCustomization } from '../../../store/investor/action';
import { configToIds, getDefaultColumnIds } from '../DataTable/utils/columnTransformUtils';
import { DataTableColumn } from '../DataTable/DataTable.types';
import { selectSelectedColumns } from '../../../store/investor/selectors';
import CrossIcon from '../../../assets/Svg/CrossIcon';
import { columnIconGrey, columnIconPurple, PURPLE_CROSS } from '../../../assets/hostedassets';

export interface ColumnCustomizationButtonProps {
    enabled?: boolean;
    filterSourceKey?: string;
    currentColumns?: DataTableColumn[] | string[];
    areColumnsCustomized?: boolean;
    style?: React.CSSProperties;
    variant?: 'primary' | 'secondary' | 'tertiary' | 'white' | 'blue' | 'purple' | 'orange';
    onCustomColumnsCleared?: () => void;
    masterColumnConfig?: DataTableColumn[]; // Master config for resetting to base configuration
}

const ColumnCustomizationButton: React.FC<ColumnCustomizationButtonProps> = ({
    enabled = false,
    filterSourceKey = '',
    currentColumns = [],
    areColumnsCustomized = false,
    style = {},
    variant = 'secondary',
    onCustomColumnsCleared,
    masterColumnConfig = [],
}) => {
    const dispatch = useDispatch();
    const selectedColumnsFromRedux = useSelector(selectSelectedColumns);

    const handleOpenColumnCustomization = () => {
        if (enabled && filterSourceKey) {
            // Handle both DataTableColumn[] and string[] types
            let currentColumnIds: string[] = [];
            if (currentColumns.length > 0) {
                if (typeof currentColumns[0] === 'string') {
                    // currentColumns is already string[] (column IDs)
                    currentColumnIds = currentColumns as string[];
                } else {
                    // currentColumns is DataTableColumn[], convert to IDs
                    currentColumnIds = configToIds(currentColumns as DataTableColumn[]);
                }
            } else if (selectedColumnsFromRedux.length > 0) {
                // Use Redux state if currentColumns is empty
                currentColumnIds = selectedColumnsFromRedux;
            }

            dispatch(
                openColumnCustomizationDrawer({
                    sourceKey: filterSourceKey,
                    selectedColumns: currentColumnIds,
                }),
            );
        }
    };

    const handleClearCustomColumns = (e: React.MouseEvent) => {
        e.stopPropagation();

        // Reset to base configuration
        if (masterColumnConfig && masterColumnConfig.length > 0) {
            const defaultColumnIds = getDefaultColumnIds(masterColumnConfig);
            dispatch(resetColumnCustomization({ defaultColumns: defaultColumnIds }));
        }

        // Also call the callback for backward compatibility
        if (onCustomColumnsCleared) {
            onCustomColumnsCleared();
        }
    };

    if (!enabled) {
        return null;
    }

    const buttonStyle = {
        marginTop: 0,
        borderRadius: '0.25rem',
        height: '2.75rem',
        ...(!areColumnsCustomized
            ? {}
            : {
                  background: '#F2E8FF',
                  border: '1px solid #F2E8FF',
              }),
        ...style,
    };

    const iconStyles = areColumnsCustomized
        ? { paddingRight: '0.5rem', borderRight: '1px solid #C7C7C7', width: '1.5rem' }
        : { margin: '0' };

    const endIconStyles = { height: '0.8rem', marginLeft: '0.25rem' };

    return (
        <Button
            text={
                areColumnsCustomized ? (
                    <span style={{ color: '#663399', marginLeft: '0.5rem' }}>Columns</span>
                ) : (
                    <span style={{ color: '#4D4D4D', marginLeft: '0.5rem' }}>Columns</span>
                )
            }
            onClick={handleOpenColumnCustomization}
            style={buttonStyle}
            icon={areColumnsCustomized ? columnIconPurple : columnIconGrey}
            variant={variant}
            iconStyles={iconStyles}
            endIcon={areColumnsCustomized ? PURPLE_CROSS : null}
            endIconStyles={endIconStyles}
            endIconClick={areColumnsCustomized ? handleClearCustomColumns : undefined}
        />
    );
};

export default ColumnCustomizationButton;
